// src/components/customer360/LogContactModal.jsx
// The app-wide "Log a contact" panel: floating and non-blocking (not a modal),
// mounted once at the root. Any screen opens it with openContactLog() from
// utils/contactLogStore; the draft lives in that store, so the rep can move
// anywhere on the site mid-call and the panel stays put until Save / Cancel.
// Minimize collapses it to a title bar. "Talking points" asks the AI for call
// prep from past conversations + purchase gaps.
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Button } from "../ui/button";
import { Loader2, Minus, ChevronUp, ChevronDown, X, Phone, Sparkles, Plus, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../apiFetch";
import { localInputToISO } from "../../utils/dates";
import { errMsg } from "./util";
import { useVoiceNote, VoiceRecordControls } from "../OutreachPanel";
import {
  closeContactLog, getContactLogState, notifyContactSaved, setContactAccountInfo, setContactLogMinimized,
  setTalkingPoints, subscribeContactLog, updateContactDraft,
} from "../../utils/contactLogStore";

const CHANNELS = [
  ["phone", "Phone call"],
  ["email", "Email (sent elsewhere)"],
  ["meeting", "Meeting"],
  ["visit", "Visit"],
  ["text", "Text message"],
  ["other", "Other"],
];

// Signed out (or another tab logged out) → hide the panel.
const signedIn = () => {
  try { return !!localStorage.getItem("cbc_user"); } catch { return true; }
};

const SECTIONS = [
  ["recap", "Last time"],
  ["followUps", "Follow up on"],
  ["opportunities", "Bring up"],
  ["questions", "Ask"],
  ["avoid", "Careful with"],
];

function TalkingPoints({ accountId, draft, talking }) {
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(true);

  const generate = async (refresh) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/api/cs/companion-kits/customer/${encodeURIComponent(accountId)}/talking-points${refresh ? "?refresh=1" : ""}`,
        { method: "POST", body: { purpose: draft.purpose || draft.subject || "" } },
      );
      if (!res?.ok) throw new Error(res?.error || "Failed");
      setTalkingPoints({ points: res.points, basis: res.basis || null, generatedAt: res.generatedAt, accountId });
      setShown(true);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't write talking points"));
    } finally {
      setBusy(false);
    }
  };

  const addToNotes = (text) => updateContactDraft((d) => ({ notes: d.notes ? `${d.notes}\n☐ ${text}` : `☐ ${text}` }));
  const points = talking?.accountId === accountId ? talking.points : null;
  const basis = talking?.basis;

  return (
    <div className="mb-3 rounded-lg border border-violet-200 bg-violet-50/50">
      <div className="flex items-center gap-2 px-3 py-1.5">
        <Sparkles className="h-3.5 w-3.5 text-violet-600" />
        <span className="text-xs font-semibold text-violet-900 flex-1">Talking points</span>
        {points ? (
          <>
            <button type="button" className="p-1 rounded hover:bg-violet-100 text-violet-700" title="Write them again" disabled={busy} onClick={() => generate(true)}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </button>
            <button type="button" className="p-1 rounded hover:bg-violet-100 text-violet-700" title={shown ? "Hide" : "Show"} onClick={() => setShown((s) => !s)}>
              {shown ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </>
        ) : null}
      </div>
      {!points ? (
        <div className="px-3 pb-2 flex gap-2 items-center">
          <input
            className="flex-1 border rounded px-2 py-1 text-xs bg-white"
            value={draft.purpose}
            onChange={(e) => updateContactDraft({ purpose: e.target.value })}
            placeholder="Why are you calling? (optional)"
          />
          <Button size="sm" className="h-7 text-xs gap-1 bg-violet-600 hover:bg-violet-700" disabled={busy} onClick={() => generate(false)}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Generate
          </Button>
        </div>
      ) : shown ? (
        <div className="px-3 pb-2 space-y-2 text-xs max-h-[38vh] overflow-y-auto">
          {SECTIONS.map(([key, label]) => {
            const list = points[key] || [];
            if (!list.length) return null;
            return (
              <div key={key}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-800">{label}</div>
                <ul className="mt-0.5 space-y-0.5">
                  {list.map((p, i) => {
                    const text = typeof p === "string" ? p : p.point;
                    return (
                      <li key={i} className="group flex gap-1.5 items-start">
                        <span className="text-violet-400 mt-[1px]">•</span>
                        <span className="flex-1 text-slate-800">
                          {text}
                          {typeof p !== "string" && p.why ? <span className="block text-[11px] text-slate-500">{p.why}</span> : null}
                        </span>
                        <button type="button" className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-violet-100 text-violet-700" title="Add to summary as a checklist line" onClick={() => addToNotes(text)}>
                          <Plus className="h-3 w-3" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          {basis ? (
            <div className="text-[10px] text-slate-400">
              From {basis.contacts || 0} logged contacts, {basis.notes || 0} notes, {basis.dealerNotes || 0} dealer notes and {basis.emails || 0} emails (last {basis.months || 6} months), plus purchase history.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function LogContactModal() {
  const s = useSyncExternalStore(subscribeContactLog, getContactLogState);
  const { open, minimized, account, draft, talking } = s;
  const accountId = account?.accountId || null;
  const [saving, setSaving] = useState(false);
  const [phones, setPhones] = useState([]);
  const voice = useVoiceNote((text) => updateContactDraft((d) => ({ notes: d.notes ? `${d.notes}\n\n${text}` : text })));

  // Name, number and phone numbers for the header (openers may only know the id).
  useEffect(() => {
    if (!open || !accountId) { setPhones([]); return undefined; }
    let cancelled = false;
    apiFetch(`/api/admin/accounts/${encodeURIComponent(accountId)}/contacts`)
      .then((res) => {
        if (cancelled || !res?.ok) return;
        const a = res.account || {};
        const list = [];
        if (a.Phone) list.push({ label: "Office", number: a.Phone });
        for (const c of res.contacts || []) if (c.Mobile && !list.some((p) => p.number === c.Mobile)) list.push({ label: c.Name || "Contact", number: c.Mobile });
        setPhones(list.slice(0, 4));
        const st = getContactLogState();
        if (!st.account?.accountName || !st.account?.accountNumber) {
          setContactAccountInfo({ accountName: st.account?.accountName || a.Name || "", accountNumber: st.account?.accountNumber || String(a.AccountNumber || "").trim() });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, accountId]);

  const set = (k) => (e) => updateContactDraft({ [k]: e.target.value });

  const save = async () => {
    if (!draft.subject.trim() && !draft.notes.trim()) {
      toast.warn("Add a subject or a short summary");
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/admin/accounts/${encodeURIComponent(accountId)}/contact-log`, {
        method: "POST",
        body: {
          channel: draft.channel,
          direction: draft.direction,
          subject: draft.subject.trim(),
          noteText: draft.notes.trim(),
          contactedAt: draft.contactedAt ? localInputToISO(draft.contactedAt) : null,
          nextFollowUpAt: draft.nextFollowUp || null,
        },
      });
      if (!res?.ok) throw new Error(res?.error || "Failed");
      toast.success("Contact logged");
      notifyContactSaved(accountId, res);
      closeContactLog();
    } catch (e) {
      toast.error(errMsg(e, "Failed to log contact"));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if ((draft.notes.trim() || draft.subject.trim()) && !window.confirm("Discard this contact note?")) return;
    closeContactLog();
  };

  if (!open || !accountId || !signedIn()) return null;
  const title = account.accountName || "Log a contact";
  return (
    <div
      role="dialog"
      aria-label="Log a contact"
      className="fixed bottom-4 right-4 z-[60] w-[min(480px,calc(100vw-2rem))] rounded-xl bg-white shadow-2xl border border-slate-200"
    >
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-slate-50 rounded-t-xl">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setContactLogMinimized(!minimized)}>
          <div className="font-semibold text-slate-900 text-sm truncate">
            {title}
            {account.accountNumber ? <span className="ml-1.5 text-xs font-normal text-slate-400">#{account.accountNumber}</span> : null}
          </div>
          {minimized ? <div className="text-[11px] text-slate-500">Contact log · {voice.recording ? "recording…" : "draft open"}</div> : null}
        </div>
        <button type="button" className="p-1 rounded hover:bg-slate-200 text-slate-600" title={minimized ? "Expand" : "Minimize"} onClick={() => setContactLogMinimized(!minimized)}>
          {minimized ? <ChevronUp className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
        </button>
        <button type="button" className="p-1 rounded hover:bg-slate-200 text-slate-600" title="Close" onClick={cancel} disabled={saving}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className={minimized ? "hidden" : "p-4 max-h-[calc(100vh-6rem)] overflow-y-auto"}>
        {phones.length ? (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {phones.map((p) => (
              <a key={p.number} href={`tel:${p.number.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-50" title={`Call ${p.label}`}>
                <Phone className="h-3 w-3" /> {p.number} <span className="text-slate-400">{p.label}</span>
              </a>
            ))}
          </div>
        ) : null}
        <TalkingPoints accountId={accountId} draft={draft} talking={talking} />
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-xs text-slate-500">Type</span>
            <select className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={draft.channel} onChange={set("channel")}>
              {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Direction</span>
            <select className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={draft.direction} onChange={set("direction")}>
              <option value="outbound">We reached out</option>
              <option value="inbound">Customer reached us</option>
            </select>
          </label>
          <label className="block col-span-2">
            <span className="text-xs text-slate-500">Subject</span>
            <input className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={draft.subject} onChange={set("subject")} placeholder="e.g. Followed up on open quote Q0001234" />
          </label>
          <label className="block col-span-2">
            <span className="text-xs text-slate-500">What was talked about</span>
            <textarea className={`mt-1 w-full border rounded px-2 py-1.5 text-sm ${draft.notes ? "h-[35vh] min-h-24" : "h-24"}`} value={draft.notes} onChange={set("notes")} placeholder="What was discussed, what they need, anything to remember — or record a voice note" />
            <div className="mt-1"><VoiceRecordControls voice={voice} /></div>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">When</span>
            <input type="datetime-local" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={draft.contactedAt} onChange={set("contactedAt")} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Next follow-up (optional)</span>
            <input type="date" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={draft.nextFollowUp} onChange={set("nextFollowUp")} />
          </label>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={cancel} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving || voice.recording || voice.transcribing}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
